import { Plus, Minus } from 'lucide-react'
import { clsx } from 'clsx'
import { canAddPlayer } from '../squadUtils'
import type { PlayerListItem, SquadDraft, AddBlockReason } from '../types'
import { Flag } from '@/shared/components/ui/Flag'
import { PlayerStatsAccordion } from './PlayerStatsAccordion'
import { InjuryBadge } from './InjuryBadge'
import styles from './PlayerCard.module.css'

// ── Props ──────────────────────────────────────────────────────────────────────

type PlayerCardProps = {
  player:       PlayerListItem
  draft:        SquadDraft
  isWindowOpen: boolean
  isInSquad:    boolean
  onAdd:        (player: PlayerListItem) => void
  onRemove:     (playerId: string) => void
  isExpanded:   boolean
  onExpand:     () => void
  isMobile:     boolean
}

// ── Helpers ────────────────────────────────────────────────────────────────────

const POSITION_CLASS: Record<string, string | undefined> = {
  GK:  styles.posGk,
  DEF: styles.posDef,
  MID: styles.posMid,
  FWD: styles.posFwd,
}

const ADD_BLOCK_LABELS: Record<AddBlockReason, string> = {
  window_closed:          'Janela fechada',
  already_in_squad:       'Já na escalação',
  squad_full:             'Elenco completo',
  position_limit_reached: 'Limite de posição',
  budget_exceeded:        'Orçamento insuficiente',
  player_eliminated:      'Eliminado',
  player_unavailable:     'Indisponível',
}

// ── Component ──────────────────────────────────────────────────────────────────

export function PlayerCard({
  player,
  draft,
  isWindowOpen,
  isInSquad,
  onAdd,
  onRemove,
  isExpanded,
  onExpand,
  isMobile,
}: PlayerCardProps) {
  const addCheck    = canAddPlayer(draft, player, isWindowOpen)
  const canAdd      = addCheck.canAdd
  const blockReason = !canAdd && !isInSquad ? addCheck.reason : null
  const posClass    = POSITION_CLASS[player.position]

  const avgDisplay  = player.avgPoints != null ? player.avgPoints.toFixed(1) : '—'

  // Desktop accordion: expanded row has no border-bottom (accordion provides its own)
  const showAccordion = isExpanded && !isMobile

  return (
    <div className={styles.wrapper}>
      <div
        className={clsx(
          styles.row,
          styles.rowClickable,
          isInSquad           && styles.inSquad,
          player.isEliminated && styles.eliminated,
          showAccordion       && styles.rowExpanded,
        )}
        role="listitem"
        onClick={onExpand}
        aria-expanded={showAccordion}
      >
        {/* Position badge */}
        <div className={clsx(styles.posBadge, posClass)} aria-label={player.position}>
          {player.position}
        </div>

        {/* Name + team */}
        <div className={styles.info}>
          <div className={styles.nameRow}>
            <span className={styles.name} title={player.name}>
              {player.displayName}
            </span>
            <InjuryBadge status={player.injuryStatus} isEliminated={player.isEliminated} />
          </div>
          <span className={styles.meta}>
            <span className={styles.teamCode}>{player.teamCode}</span>
            <Flag code={player.teamCode} title={player.teamName} size={14} />
            {/* Next opponent shown inline next to the team code (no separate column) */}
            {player.nextOpponent && (
              <span className={styles.metaNext} title="Próximo adversário">vs {player.nextOpponent}</span>
            )}
          </span>
        </div>

        {/* Pts (total) */}
        <span className={clsx(styles.cell, styles.cellPts)} title="Pontos totais">
          {player.totalPoints}
        </span>

        {/* Last GW */}
        <span className={clsx(styles.cell, styles.cellLast)} title="Pontos da última rodada">
          {player.gwPoints ?? '—'}
        </span>

        {/* Avg */}
        <span className={clsx(styles.cell, styles.cellAvg)} title="Média por rodada">
          {avgDisplay}
        </span>

        {/* Price */}
        <span className={clsx(styles.cell, styles.cellPrice)} title="Preço">
          ${player.price.toFixed(1)}
        </span>

        {/* Action */}
        <div className={styles.actionWrap}>
          {isInSquad ? (
            <button
              className={clsx(styles.actionBtn, styles.removeBtn)}
              onClick={(e) => { e.stopPropagation(); onRemove(player.playerId) }}
              disabled={!isWindowOpen}
              aria-label={`Remover ${player.displayName}`}
              title={isWindowOpen ? undefined : 'Janela fechada'}
            >
              <Minus size={13} strokeWidth={2.5} />
            </button>
          ) : (
            <button
              className={clsx(
                styles.actionBtn,
                styles.addBtn,
                !canAdd && styles.actionBtnBlocked,
              )}
              onClick={(e) => { e.stopPropagation(); canAdd && onAdd(player) }}
              disabled={!canAdd}
              aria-label={canAdd
                ? `Adicionar ${player.displayName}`
                : (blockReason ? ADD_BLOCK_LABELS[blockReason] : undefined)}
              title={blockReason ? ADD_BLOCK_LABELS[blockReason] : undefined}
            >
              <Plus size={13} strokeWidth={2.5} />
            </button>
          )}
        </div>
      </div>

      {/* Desktop inline accordion */}
      {showAccordion && (
        <PlayerStatsAccordion
          player={player}
          isInSquad={isInSquad}
          isWindowOpen={isWindowOpen}
          onAdd={onAdd}
          onRemove={onRemove}
          onClose={onExpand}
        />
      )}
    </div>
  )
}
